//
// ChatAlert Component - Displays alert messages in chat
//
// Message example:
//
// message = {
//   alrtTxt: 'Nous recherchons la personne suivante...:',
//   msgType: 'alert',
//   alrtForm: {
//     qstnId: 'policeSketch',
//   },
//   crUid: 16,
//   msgRdatim1: '2022-10-17 15:16:21.0',
//   msgId: 286,
//   msgUid: 286,
//   msgFileCnt: 1,
//   crUidAdd: 16,
//   tgtTypes: ['c_rescue', 'c_users'],
//   id: 6,
// };
//
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useState,
  useEffect,
  memo,
  useMemo,
} from 'react';

import {
  Megaphone,
} from '../../../../assets/js/svg/gui';
import { displayJson } from '../../../../utils';
import {
  Column,
  Row,
} from '../../../display';
import { useIsMounted } from '../../../React';
import { FormPoliceSketchContent } from '../../forms';

import ChatMessageControls from './ChatMessageControls';
import {
  ChatAuthorIcon,
  ChatAuthorName,
  ChatDate,
  ChatItemClick,
  MessageFrame,
  MessageText,
} from './components';

const ChatAlert = memo(({
  registerChild,
  translate,
  style,
  message,
  author,
  initials,
  crBlocked,
  color,
  gcatCode,
  getMedia,
  csid,
  piid,
  visibleInThisGathering = true,
  isScrolling,
  isVisible,
  grpGpid,
  grpLbl,
  grpNbr,
  groupsMap,
  imagesMap,
  prtnrLogoUrl,
  selectGathering,
}) => {
  const [showControls, setShowControls] = useState(false);
  const {
    id,
    gthrId,
    msgId,
    msgRdatim1,
    alrtTxt,
    alrtForm,
    msgFileCnt,
  } = message;
  const {
    qstnId,
    sketch,
  } = alrtForm;
  const [pic, setPic] = useState(msgFileCnt
    ? getMedia({
        csid, piid, msgId, type: 'img',
      })
    : undefined);
  const [picPending, setPicPending] = useState(!!msgFileCnt);
  const isMountedRef = useIsMounted();

  useEffect(
    () => {
      if (msgFileCnt && (!pic)) {
        getMedia(
          {
            csid,
            piid,
            msgId,
            type: 'img',
          },
          (err, newPic) => {
            if (!err) {
              if (isMountedRef.current) {
                setPic(newPic);
                setPicPending(false);
              }
            }
          },
        );
      }
    },
    [pic, msgFileCnt, csid, piid, getMedia, msgId, isMountedRef],
  );

  const content = useMemo(
    () => {
      if (qstnId === 'policeSketch') {
        return (
          <>
            <div key="text">
              <b>{alrtTxt}</b>
            </div>

            <FormPoliceSketchContent
              translate={translate}
              key="content"
              pic={pic}
              picPending={picPending}

              {...sketch}
            />

            <div key="note">
              <i>
                {translate('refrainContact', 'statements')}
              </i>
            </div>
          </>
        );
      }

      console.log(`ChatAlert message=${displayJson(message)}, qstnId=${qstnId}`);

      return (
        <span>
          <i>
            {translate(qstnId, 'questionnairePrompts')}
            {(alrtTxt) ? ` ${alrtTxt}` : null}
          </i>
          <br />
          {displayJson(message)}
        </span>
      );
    },
    [sketch, alrtTxt, message, pic, picPending, qstnId, translate],
  );

  const containerClassName = classNames(
    'w-full py-2 gap-1 flex flex-col relative items-center justify-center overflow-hidden group',
    {
      'px-6': visibleInThisGathering,
      'px-2 opacity-50': !visibleInThisGathering,
    },
  );

  return (
    <div
      ref={registerChild}
      key={id}
      className={containerClassName}
      style={style}
      // https://stackoverflow.com/a/61613591/9316077
      onMouseEnter={() => { setShowControls(true); }}
      onMouseLeave={() => { setShowControls(false); }}
    >
      <ChatItemClick
        gthrId={gthrId}
        selectGathering={selectGathering}
        visibleInThisGathering={visibleInThisGathering}
      />

      <ChatAuthorName
        translate={translate}
        author={author}
        initials={initials}
        crBlocked={crBlocked}
        gcatCode={gcatCode}
        grpNbr={grpNbr}
        prtnrLogoUrl={prtnrLogoUrl}
      />

      <Row
        start={!!author}
        end={!author}
        fullw
      >
        <ChatAuthorIcon
          author={author}
          initials={initials}
          color={color}
          gcatCode={gcatCode}
          grpGpid={grpGpid}
          grpLbl={grpLbl}
          groupsMap={groupsMap}
          imagesMap={imagesMap}
        />

        <MessageFrame
          key={id}
          author={author}
          visibleInThisGathering={visibleInThisGathering}
        >
          <MessageText
            message={(
              <>
                <Row className="w-full mb-2.5">
                  <Row className="bg-pxCaseOrangeLeft rounded-md text-white px-3 py-1 w-fit border border-pxCaseRedLeft shadow-button">
                    <Megaphone className="scale-x-[-1] fill-white h-6" />
                    <div className="whitespace-nowrap mx-3">
                      <b><i>{translate('alert')}</i></b>
                    </div>
                    <Megaphone className="fill-white h-6" />
                  </Row>
                </Row>

                {content}
              </>
            )}
            author={author}
          />
        </MessageFrame>

        <Column
          className={classNames(
            'self-start',
            {
              'order-2': !author,
              'order-4': author,
            },
          )}
          gap={1}
          crossStart={author}
          crossEnd={!author}
        >
          <ChatDate
            dtime={msgRdatim1}
            myMessage={!author}
          />

          <ChatMessageControls
            translate={translate}
            myMessage={!author}
            showControls={showControls}
            author={author}
            message={message}
            csid={csid}
            isScrolling={isScrolling}
            isVisible={isVisible}
          />
        </Column>

      </Row>
    </div>
  );
});
ChatAlert.displayName = 'ChatAlert';

ChatAlert.propTypes = {
  registerChild: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  style: PropTypes.shape({}).isRequired,
  author: PropTypes.string,
  initials: PropTypes.string,
  crBlocked: PropTypes.number,
  color: PropTypes.string,
  gcatCode: PropTypes.string,
  getMedia: PropTypes.func.isRequired,
  csid: PropTypes.string.isRequired,
  piid: PropTypes.string,
  visibleInThisGathering: PropTypes.bool,
  isScrolling: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool.isRequired,
  grpGpid: PropTypes.string,
  grpLbl: PropTypes.string,
  grpNbr: PropTypes.number,
  groupsMap: PropTypes.instanceOf(Map).isRequired,
  imagesMap: PropTypes.instanceOf(Map).isRequired,
  selectGathering: PropTypes.func,
  message: PropTypes.shape({
    id: PropTypes.number.isRequired,
    gthrId: PropTypes.number,
    msgId: PropTypes.number.isRequired,
    msgRdatim1: PropTypes.string.isRequired,
    note: PropTypes.string,
    unknown: PropTypes.bool,
    msgFileCnt: PropTypes.number,
    alrtTxt: PropTypes.string,
    alrtForm: PropTypes.shape({
      qstnId: PropTypes.string.isRequired,
      sketch: PropTypes.shape({
        fname: PropTypes.string,
        lname: PropTypes.string,
        age: PropTypes.number,
        gender: PropTypes.string,
        rltn: PropTypes.string,
      }),
    }).isRequired,
  }).isRequired,
  prtnrLogoUrl: PropTypes.string,
};

export default ChatAlert;
